/**
 * Batch chat import sheet.
 *
 * Two-step UX:
 *   1) User pastes one ref per line in a textarea.
 *      Live counter + format hint + parse-preview ("✓ resolved" / "✗ error")
 *      via /chats/resolve before they commit.
 *   2) Press "Импортировать" → /chats/import resolves *and* persists in one
 *      pass, with optional guarantor auto-detect. We show per-line result
 *      tiles (added / duplicate / failed / needs_join) and totals at top.
 */

import { motion, AnimatePresence } from "framer-motion";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CornerDownRight,
  Lock,
  Loader2,
  ShieldCheck,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/design";
import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";
import { ApiError } from "@/api/client";
import {
  useImportChats,
  useResolvePreview,
  type ImportResultItem,
  type ResolvePreviewItem,
} from "@/api/chats";

interface ChatImportSheetProps {
  open: boolean;
  onClose: () => void;
}

export function ChatImportSheet({ open, onClose }: ChatImportSheetProps) {
  const [raw, setRaw] = useState("");
  const [detectGuarantor, setDetectGuarantor] = useState(true);
  const [preview, setPreview] = useState<ResolvePreviewItem[] | null>(null);
  const [results, setResults] = useState<ImportResultItem[] | null>(null);
  const previewDebounce = useRef<number | null>(null);

  const resolveMut = useResolvePreview();
  const importMut = useImportChats();

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setRaw("");
    setPreview(null);
    setResults(null);
    resolveMut.reset();
    importMut.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const refs = useMemo(
    () =>
      raw
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean),
    [raw],
  );

  // Debounced preview while user types — only when no results yet
  useEffect(() => {
    if (results) return;
    if (previewDebounce.current) window.clearTimeout(previewDebounce.current);
    if (refs.length === 0) {
      setPreview(null);
      return;
    }
    previewDebounce.current = window.setTimeout(async () => {
      try {
        const res = await resolveMut.mutateAsync({ refs });
        setPreview(res.items);
      } catch {
        // Surface error inline, but don't toast for preview
        setPreview(null);
      }
    }, 500);
    return () => {
      if (previewDebounce.current) window.clearTimeout(previewDebounce.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refs.join("\n")]);

  const handleImport = async () => {
    if (refs.length === 0) return;
    haptic.impact("medium");
    try {
      const res = await importMut.mutateAsync({
        refs,
        detect_guarantor: detectGuarantor,
      });
      setResults(res.items);
      haptic.notify("success");
      if (res.added > 0) {
        toast.success(`Добавлено ${res.added}`);
      } else {
        toast(res.skipped_existing > 0 ? "Все уже добавлены" : "Ничего не добавили");
      }
    } catch (err) {
      haptic.notify("error");
      if (err instanceof ApiError && err.status === 412) {
        toast.error("Подключите аккаунт в разделе «Аккаунты»");
      } else if (err instanceof ApiError && err.status === 429) {
        toast.error("Telegram попросил подождать. Попробуйте позже.");
      } else {
        toast.error((err as Error)?.message || "Не удалось импортировать");
      }
    }
  };

  const handleClose = () => {
    if (importMut.isPending) return;
    onClose();
  };

  const totals = results
    ? {
        added: results.filter((r) => r.ok && r.chat).length,
        duplicate: results.filter((r) => r.ok && !r.chat).length,
        failed: results.filter((r) => !r.ok).length,
      }
    : null;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleClose}
          />
          <motion.div
            className={cn(
              "fixed inset-x-0 bottom-0 top-8 z-50",
              "glass-strong rounded-t-[28px] overflow-hidden",
              "border-t border-white/[0.08] flex flex-col",
            )}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 pt-4 pb-3 shrink-0 border-b border-white/[0.04]">
              <button
                onClick={handleClose}
                className="size-9 grid place-items-center rounded-full bg-white/[0.04] hover:bg-white/[0.08] transition-colors"
                aria-label="Закрыть"
              >
                <ArrowLeft className="size-4" />
              </button>
              <div className="text-base font-semibold">
                {results ? "Результат" : "Добавить чаты"}
              </div>
              {results ? (
                <Button size="sm" onClick={handleClose}>
                  Готово
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={handleImport}
                  disabled={refs.length === 0 || importMut.isPending}
                  loading={importMut.isPending}
                >
                  Импорт {refs.length > 0 && `(${refs.length})`}
                </Button>
              )}
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
              {!results ? (
                <Composer
                  raw={raw}
                  onChange={setRaw}
                  preview={preview}
                  previewing={resolveMut.isPending}
                  detectGuarantor={detectGuarantor}
                  setDetectGuarantor={setDetectGuarantor}
                  refsCount={refs.length}
                />
              ) : (
                <ResultsView totals={totals!} items={results} />
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// ============================================================
// Composer (textarea + live preview)
// ============================================================

function Composer({
  raw,
  onChange,
  preview,
  previewing,
  detectGuarantor,
  setDetectGuarantor,
  refsCount,
}: {
  raw: string;
  onChange: (s: string) => void;
  preview: ResolvePreviewItem[] | null;
  previewing: boolean;
  detectGuarantor: boolean;
  setDetectGuarantor: (v: boolean) => void;
  refsCount: number;
}) {
  return (
    <div className="space-y-4">
      {/* Format hint */}
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 space-y-2">
        <div className="text-xs uppercase tracking-wider text-text-dim font-medium">
          Форматы ссылок
        </div>
        <div className="grid grid-cols-1 gap-1 text-[13px] text-text-muted">
          <HintRow tag="@username" />
          <HintRow tag="t.me/username" />
          <HintRow tag="t.me/username/123" subtitle="форум-тема" />
          <HintRow tag="t.me/+invitehash" subtitle="приватный инвайт" />
          <HintRow tag="t.me/c/12345/topic" subtitle="приватный канал" />
        </div>
      </div>

      {/* Textarea */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between px-1">
          <div className="text-xs uppercase tracking-wider text-text-dim font-medium">
            Ссылки по одной на строку
          </div>
          <div className="text-[10px] text-text-faint tabular-nums">
            {refsCount} стр.
          </div>
        </div>
        <textarea
          value={raw}
          onChange={(e) => onChange(e.target.value)}
          placeholder={"@chat1\nt.me/chat2\nt.me/+invitehash"}
          rows={6}
          spellCheck={false}
          className={cn(
            "w-full p-4 rounded-2xl resize-none",
            "bg-surface border border-white/[0.06]",
            "text-sm font-mono leading-relaxed outline-none focus:border-accent/40 transition-colors",
            "placeholder:text-text-faint",
          )}
        />
      </div>

      {/* Guarantor toggle */}
      <button
        onClick={() => setDetectGuarantor(!detectGuarantor)}
        className={cn(
          "w-full glass rounded-2xl p-3.5 flex items-center gap-3",
          "hover:bg-white/[0.05] transition-colors text-left",
        )}
      >
        <div
          className={cn(
            "size-10 rounded-xl grid place-items-center shrink-0",
            detectGuarantor ? "bg-accent-soft text-accent" : "bg-white/[0.04] text-text-dim",
          )}
        >
          <ShieldCheck className="size-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">Автоопределение гаранта</div>
          <div className="text-xs text-text-muted">
            Парсим закреп и последние сообщения
          </div>
        </div>
        <div
          className={cn(
            "relative w-10 h-6 rounded-full transition-colors shrink-0",
            detectGuarantor ? "bg-accent" : "bg-white/[0.08]",
          )}
        >
          <motion.div
            animate={{ x: detectGuarantor ? 16 : 0 }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
            className="absolute top-0.5 left-0.5 size-5 rounded-full bg-white"
          />
        </div>
      </button>

      {/* Preview list */}
      {refsCount > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between px-1">
            <div className="text-xs uppercase tracking-wider text-text-dim font-medium">
              Превью
            </div>
            {previewing && (
              <div className="text-[10px] text-text-muted flex items-center gap-1">
                <Loader2 className="size-3 animate-spin" />
                Проверяем
              </div>
            )}
          </div>

          {preview && preview.length > 0 ? (
            <div className="space-y-1.5">
              {preview.map((p, i) => (
                <PreviewRow key={i} item={p} />
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-white/[0.06] bg-white/[0.01] p-4 text-center text-xs text-text-dim">
              Превью появится, как только ссылки будут разрешены
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HintRow({ tag, subtitle }: { tag: string; subtitle?: string }) {
  return (
    <div className="flex items-center gap-2">
      <CornerDownRight className="size-3 text-text-dim" />
      <code className="text-[12.5px] text-text">{tag}</code>
      {subtitle && <span className="text-[11px] text-text-faint">— {subtitle}</span>}
    </div>
  );
}

function PreviewRow({ item }: { item: ResolvePreviewItem }) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 px-3 py-2 rounded-xl",
        "border",
        item.ok
          ? "border-success/15 bg-success/[0.04]"
          : "border-danger/15 bg-danger/[0.04]",
      )}
    >
      <div
        className={cn(
          "size-7 rounded-lg grid place-items-center shrink-0",
          item.ok ? "bg-success/15 text-success" : "bg-danger/15 text-danger",
        )}
      >
        {item.ok ? <Check className="size-3.5" /> : <X className="size-3.5" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium truncate">
          {item.title || item.original}
        </div>
        <div className="text-[11px] text-text-muted truncate flex items-center gap-1.5">
          <span className="truncate">{item.chat_ref || item.original}</span>
          {item.needs_join && (
            <span className="inline-flex items-center gap-0.5 text-warning shrink-0">
              <Lock className="size-2.5" />
              вступить
            </span>
          )}
        </div>
        {item.error && (
          <div className="text-[11px] text-danger mt-0.5 truncate">{item.error}</div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Results
// ============================================================

function ResultsView({
  totals,
  items,
}: {
  totals: { added: number; duplicate: number; failed: number };
  items: ImportResultItem[];
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-2">
        <SummaryTile
          label="Добавлено"
          value={totals.added}
          variant="success"
        />
        <SummaryTile
          label="Уже есть"
          value={totals.duplicate}
          variant="muted"
        />
        <SummaryTile
          label="Ошибки"
          value={totals.failed}
          variant="danger"
        />
      </div>

      <div className="space-y-1.5">
        {items.map((it, i) => (
          <ResultRow key={i} item={it} />
        ))}
      </div>
    </div>
  );
}

function SummaryTile({
  label,
  value,
  variant,
}: {
  label: string;
  value: number;
  variant: "success" | "danger" | "muted";
}) {
  return (
    <div
      className={cn(
        "rounded-2xl border p-3",
        variant === "success" && "border-success/20 bg-success/[0.06]",
        variant === "danger" && "border-danger/20 bg-danger/[0.06]",
        variant === "muted" && "border-white/[0.06] bg-white/[0.02]",
      )}
    >
      <div className="text-[10px] uppercase tracking-wider text-text-dim font-medium">
        {label}
      </div>
      <div
        className={cn(
          "text-[22px] leading-none font-semibold tabular-nums tracking-tight mt-1",
          variant === "success" && "text-success",
          variant === "danger" && "text-danger",
        )}
      >
        {value}
      </div>
    </div>
  );
}

function ResultRow({ item }: { item: ImportResultItem }) {
  const status: "added" | "duplicate" | "failed" | "needs_join" = !item.ok
    ? "failed"
    : !item.chat
    ? "duplicate"
    : item.needs_join
    ? "needs_join"
    : "added";

  const meta = {
    added: { icon: Check, color: "success", label: "Добавлен" },
    duplicate: { icon: X, color: "muted", label: "Уже добавлен" },
    failed: { icon: AlertTriangle, color: "danger", label: "Ошибка" },
    needs_join: { icon: Lock, color: "warning", label: "Вступите вручную" },
  }[status];

  const Icon = meta.icon;

  return (
    <div
      className={cn(
        "flex items-center gap-3 px-3 py-2 rounded-xl border",
        meta.color === "success" && "border-success/15 bg-success/[0.04]",
        meta.color === "danger" && "border-danger/15 bg-danger/[0.04]",
        meta.color === "warning" && "border-warning/15 bg-warning/[0.04]",
        meta.color === "muted" && "border-white/[0.06] bg-white/[0.02]",
      )}
    >
      <div
        className={cn(
          "size-7 rounded-lg grid place-items-center shrink-0",
          meta.color === "success" && "bg-success/15 text-success",
          meta.color === "danger" && "bg-danger/15 text-danger",
          meta.color === "warning" && "bg-warning/15 text-warning",
          meta.color === "muted" && "bg-white/[0.04] text-text-dim",
        )}
      >
        <Icon className="size-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium truncate">
          {item.chat?.title || item.original}
        </div>
        <div className="text-[11px] text-text-muted truncate">
          {item.error || item.chat?.chat_ref || item.original}
        </div>
        {item.guarantor && (
          <div className="text-[10px] text-accent flex items-center gap-1 mt-0.5">
            <ShieldCheck className="size-2.5" />@{item.guarantor}
          </div>
        )}
      </div>
      <span
        className={cn(
          "text-[10px] uppercase tracking-wider font-medium shrink-0",
          meta.color === "success" && "text-success",
          meta.color === "danger" && "text-danger",
          meta.color === "warning" && "text-warning",
          meta.color === "muted" && "text-text-dim",
        )}
      >
        {meta.label}
      </span>
    </div>
  );
}
